import _objectWithoutProperties from "E:\\Code\\ui\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "E:\\Code\\ui\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/slicedToArray";
import _objectSpread from "E:\\Code\\ui\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/objectSpread";
import * as React from 'react';
import { makeUseContext, useLightboxState, useTimeouts, useEvents, useController, useEventCallback, cleanup, createIcon, useLightboxProps, useLoseFocus, IconButton, addToolbarButton, createModule } from '../../index.js';
import { SLIDE_STATUS_LOADING, SLIDE_STATUS_PLAYING, ACTIVE_SLIDE_LOADING, ACTIVE_SLIDE_PLAYING, ACTIVE_SLIDE_ERROR, SLIDE_STATUS_ERROR, ACTIVE_SLIDE_COMPLETE, SLIDE_STATUS_COMPLETE, PLUGIN_SLIDESHOW } from '../../types.js';
var defaultSlideshowProps = {
  autoplay: false,
  delay: 3000,
  ref: null
};
var resolveSlideshowProps = function resolveSlideshowProps(slideshow) {
  return _objectSpread({}, defaultSlideshowProps, slideshow);
};
var SlideshowContext = React.createContext(null);
var useSlideshow = makeUseContext("useSlideshow", "SlideshowContext", SlideshowContext);
function SlideshowContextProvider(_ref) {
  var slideshow = _ref.slideshow,
    finite = _ref.carousel.finite,
    on = _ref.on,
    children = _ref.children;
  var _resolveSlideshowProp = resolveSlideshowProps(slideshow),
    autoplay = _resolveSlideshowProp.autoplay,
    delay = _resolveSlideshowProp.delay,
    ref = _resolveSlideshowProp.ref;
  var wasPlaying = React.useRef(autoplay);
  var _React$useState = React.useState(autoplay),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    playing = _React$useState2[0],
    setPlaying = _React$useState2[1];
  var scheduler = React.useRef(undefined);
  var slideStatus = React.useRef(undefined);
  var _useLightboxState = useLightboxState(),
    slides = _useLightboxState.slides,
    currentIndex = _useLightboxState.currentIndex;
  var _useTimeouts = useTimeouts(),
    setTimeout = _useTimeouts.setTimeout,
    clearTimeout = _useTimeouts.clearTimeout;
  var _useEvents = useEvents(),
    subscribe = _useEvents.subscribe;
  var _useController = useController(),
    next = _useController.next;
  var disabled = slides.length === 0 || finite && currentIndex === slides.length - 1;
  var play = React.useCallback(function () {
    if (!playing && !disabled) {
      setPlaying(true);
    }
  }, [playing, disabled]);
  var pause = React.useCallback(function () {
    if (playing) {
      setPlaying(false);
    }
  }, [playing]);
  var cancelScheduler = React.useCallback(function () {
    clearTimeout(scheduler.current);
    scheduler.current = undefined;
  }, [clearTimeout]);
  var scheduleNextSlide = useEventCallback(function () {
    cancelScheduler();
    if (!playing || disabled || slideStatus.current === SLIDE_STATUS_LOADING || slideStatus.current === SLIDE_STATUS_PLAYING) {
      return;
    }
    scheduler.current = setTimeout(function () {
      if (playing) {
        slideStatus.current = undefined;
        next();
      }
    }, delay);
  });
  React.useEffect(scheduleNextSlide, [currentIndex, playing, scheduleNextSlide]);
  React.useEffect(function () {
    if (playing && disabled) {
      setPlaying(false);
    }
  }, [currentIndex, playing, disabled]);
  var onSlideshowStart = useEventCallback(function () {
    var _a;
    return (_a = on.slideshowStart) === null || _a === void 0 ? void 0 : _a.call(on);
  });
  var onSlideshowStop = useEventCallback(function () {
    var _a;
    return (_a = on.slideshowStop) === null || _a === void 0 ? void 0 : _a.call(on);
  });
  React.useEffect(function () {
    if (playing) {
      onSlideshowStart();
    } else if (wasPlaying.current) {
      onSlideshowStop();
    }
    wasPlaying.current = playing;
  }, [playing, onSlideshowStart, onSlideshowStop]);
  React.useEffect(function () {
    return cleanup(cancelScheduler, subscribe(ACTIVE_SLIDE_LOADING, function () {
      slideStatus.current = SLIDE_STATUS_LOADING;
      cancelScheduler();
    }), subscribe(ACTIVE_SLIDE_PLAYING, function () {
      slideStatus.current = SLIDE_STATUS_PLAYING;
      cancelScheduler();
    }), subscribe(ACTIVE_SLIDE_ERROR, function () {
      slideStatus.current = SLIDE_STATUS_ERROR;
      scheduleNextSlide();
    }), subscribe(ACTIVE_SLIDE_COMPLETE, function () {
      slideStatus.current = SLIDE_STATUS_COMPLETE;
      scheduleNextSlide();
    }));
  }, [subscribe, cancelScheduler, scheduleNextSlide]);
  var context = React.useMemo(function () {
    return {
      playing: playing,
      disabled: disabled,
      play: play,
      pause: pause
    };
  }, [playing, disabled, play, pause]);
  React.useImperativeHandle(ref, function () {
    return context;
  }, [context]);
  return React.createElement(SlideshowContext.Provider, {
    value: context
  }, children);
}
var PlayIcon = createIcon("Play", React.createElement("path", {
  d: "M8 5v14l11-7z"
}));
var PauseIcon = createIcon("Pause", React.createElement("path", {
  d: "M6 19h4V5H6v14zm8-14v14h4V5h-4z"
}));
function SlideshowButton() {
  var _useSlideshow = useSlideshow(),
    playing = _useSlideshow.playing,
    disabled = _useSlideshow.disabled,
    play = _useSlideshow.play,
    pause = _useSlideshow.pause;
  var _useLightboxProps = useLightboxProps(),
    render = _useLightboxProps.render;
  var focusListeners = useLoseFocus(useController().focus, disabled);
  if (render.buttonSlideshow) {
    return React.createElement(React.Fragment, null, render.buttonSlideshow({
      playing: playing,
      disabled: disabled,
      play: play,
      pause: pause
    }));
  }
  return React.createElement(IconButton, _objectSpread({
    label: playing ? "Pause" : "Play",
    icon: playing ? PauseIcon : PlayIcon,
    renderIcon: playing ? render.iconSlideshowPause : render.iconSlideshowPlay,
    onClick: playing ? pause : play,
    disabled: disabled
  }, focusListeners));
}
function Slideshow(_ref2) {
  var augment = _ref2.augment,
    addModule = _ref2.addModule;
  augment(function (_ref3) {
    var slideshow = _ref3.slideshow,
      toolbar = _ref3.toolbar,
      restProps = _objectWithoutProperties(_ref3, ["slideshow", "toolbar"]);
    return _objectSpread({
      toolbar: addToolbarButton(toolbar, PLUGIN_SLIDESHOW, React.createElement(SlideshowButton, null)),
      slideshow: resolveSlideshowProps(slideshow)
    }, restProps);
  });
  addModule(createModule(PLUGIN_SLIDESHOW, SlideshowContextProvider));
}
export { Slideshow as default };